import type { ChangeEventHandler } from "react";
import { useCallback } from "react";
import type { UseFieldArrayInsert } from "react-hook-form";

import type { PropertySchemaAssociationsFormDataBase } from "~/features/dataImportProjects/types";
import {
  csvFileToPropertySchemas,
  isCsvFile,
  orderPropertySchemasForInsert,
} from "~/features/dataImportProjects/utils/propertySchemaCsvHelpers";

type UsePropertySchemaCsvUploadProps = {
  propertySchemaAssociations: {
    insert: UseFieldArrayInsert<PropertySchemaAssociationsFormDataBase, "propertySchemaAssociations">;
  };
  showSnackbar: (message: string, severity: "error" | "success") => void;
  t: (key: string) => string;
};

export function usePropertySchemaCsvUpload({
  propertySchemaAssociations,
  showSnackbar,
  t,
}: UsePropertySchemaCsvUploadProps) {
  const handlePropertySchemaCsvUpload: ChangeEventHandler<HTMLInputElement> = useCallback(
    async (e) => {
      // Held onto because React clears `currentTarget` once the handler yields at the first await.
      const input = e.currentTarget;
      const file = input.files?.[0];
      if (!file) return;

      try {
        // 1) Guard: only CSV
        if (!isCsvFile(file)) {
          showSnackbar(t("feature.dataImportProjects.form.onlyCsvAllowed"), "error");
          return;
        }

        // 2) Parse -> Validate -> Transform (pure helper)

        const items = await csvFileToPropertySchemas(file);

        // 3) Insert into state in the correct order
        orderPropertySchemasForInsert(items).forEach((schema, index) => {
          propertySchemaAssociations.insert(index, schema);
        });
      } catch (err: unknown) {
        if (err instanceof Error && err?.message === "CSV_VALIDATION_FAILED") {
          showSnackbar(t("feature.dataImportProjects.form.propertySchemaCsvValidationFail"), "error");
        } else {
          showSnackbar(t("feature.dataImportProjects.form.propertySchemaCsvParseFail"), "error");
        }
      } finally {
        // Picking the same filename again fires no change event unless the value is reset,
        // so a corrected CSV would be silently ignored on retry.
        input.value = "";
      }
    },
    [isCsvFile, csvFileToPropertySchemas, propertySchemaAssociations, showSnackbar, t],
  );

  return { handlePropertySchemaCsvUpload };
}
